import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SERVICES } from "@/lib/services";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const statusColor = {
  open: "bg-white border-amber-200 text-slate-600",
  available: "bg-emerald-50 border-emerald-300 text-emerald-800",
  limited: "bg-amber-50 border-amber-400 text-amber-800",
  soldout: "bg-red-50 border-red-300 text-red-700",
};

export const CapacityCalendar = ({ onChange }) => {
  const [serviceId, setServiceId] = useState(SERVICES[0].id);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: rows } = await api.get("/admin/calendar", { params: { service_id: serviceId, start: fmt(first), end: fmt(last) } });
      const map = {}; rows.forEach((r) => { map[r.date] = r; }); setData(map);
    } catch { toast.error("Could not load calendar"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, month]);

  useEffect(() => { load(); }, [load]);

  const cells = [];
  for (let i = 0; i < first.getDay(); i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));

  const save = async () => {
    if (edit.limit === "" || Number(edit.limit) < 0) { toast.error("Enter a valid seat limit"); return; }
    setSaving(true);
    try {
      await api.put("/admin/capacity", { service_id: serviceId, date: edit.date, limit: Number(edit.limit) });
      toast.success("Seat limit saved");
      setEdit(null); load(); onChange?.();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete("/admin/capacity", { params: { service_id: serviceId, date: edit.date } });
      toast.success("Limit removed"); setEdit(null); load(); onChange?.();
    } catch { toast.error("Could not remove"); }
    finally { setSaving(false); }
  };

  const monthLabel = month.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="mt-2">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
        <div className="flex-1">
          <Label>Service</Label>
          <Select value={serviceId} onValueChange={setServiceId}>
            <SelectTrigger data-testid="capcal-service-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SERVICES.filter((s) => !s.hidden).map((s) => <SelectItem key={s.id} value={s.id}>{s.short}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="capcal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-cinzel font-semibold text-amber-950 w-40 text-center" data-testid="capcal-month">{monthLabel}</div>
          <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="capcal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-500 mb-1">
        {WEEK.map((w) => <div key={w}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const key = fmt(d);
          const e = data[key];
          const st = e ? e.status : "open";
          return (
            <button key={i} onClick={() => setEdit({ date: key, limit: e && e.limit != null ? String(e.limit) : "" })}
              data-testid={`capcal-day-${key}`}
              className={`aspect-square rounded-lg border p-1 text-left hover:ring-2 hover:ring-[#E65100] transition ${statusColor[st]}`}>
              <div className="text-xs font-bold">{d.getDate()}</div>
              {e && e.limit != null
                ? <div className="text-[10px] leading-tight mt-1">{e.remaining}/{e.limit}</div>
                : <div className="text-[10px] text-slate-300 mt-1">open</div>}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-400" /> Limited</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Sold out</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-amber-200" /> Open (no limit)</span>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[#E65100]" />}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="capcal-edit-dialog">
          <DialogHeader><DialogTitle className="font-cinzel text-amber-950">Set Seat Limit</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">{SERVICES.find((s) => s.id === serviceId)?.short} · <b>{edit.date}</b></div>
              <div>
                <Label>Daily Seat Limit</Label>
                <Input type="number" min="0" value={edit.limit} onChange={(ev) => setEdit((e) => ({ ...e, limit: ev.target.value }))} placeholder="e.g. 500" data-testid="capcal-limit-input" autoFocus />
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={saving} className="flex-1 bg-[#E65100] hover:bg-[#BF360C] text-white" data-testid="capcal-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}</Button>
                <Button onClick={remove} disabled={saving} variant="outline" className="text-red-600" data-testid="capcal-remove-btn"><Trash2 className="w-4 h-4 mr-1" />Remove</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
